import { useRef, useState, useEffect, useCallback, type JSX } from 'react';
import { 
  Box, 
  Paper, 
  Text, 
  Group, 
  Stack, 
  Button, 
  TextInput, 
  ActionIcon, 
  Avatar, 
  Loader,
  Tooltip,
  Menu,
  Switch
} from '@mantine/core';
import './ActionIconFix.css'; // Import the CSS fix for ActionIcon visibility
import { showNotification } from '@mantine/notifications';
import { 
  IconSend, 
  IconMicrophone, 
  IconMicrophoneOff, 
  IconVolume, 
  IconVolumeOff,
  IconDotsVertical,
  IconDownload,
  IconRefresh,
  IconPlayerPlay,
  IconPlayerPause,
  IconPlayerPlayFilled,
  IconPlayerStopFilled,
  IconSwitch
} from '@tabler/icons-react';
import { useConversation } from '../../hooks/useConversation';
import { useAudioRecording } from '../../hooks/useAudioRecording';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';
import { UpgradeModal } from '../UpgradeModal';

interface ClientReflectionChatProps {
  threadId: string;
  showUpgradeForMessage?: boolean;
  showUpgradeForVoice?: boolean;
  isMobileFullscreen?: boolean; // Flag to indicate when we're in mobile fullscreen mode
}

/**
 * Client-facing chat component for reflection guide interactions
 */
export function ClientReflectionChat({ 
  threadId, 
  showUpgradeForMessage = false, 
  showUpgradeForVoice = false,
  isMobileFullscreen = false
}: ClientReflectionChatProps): JSX.Element {
  console.log('[ClientReflectionChat] Component rendered with props:', {
    threadId,
    showUpgradeForMessage,
    showUpgradeForVoice,
    isMobileFullscreen
  });
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;
  const audioRef = useRef<HTMLAudioElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [userInput, setUserInput] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [isAutoplayEnabled, setIsAutoplayEnabled] = useState(true); // Default to true
  const [loadingPreference, setLoadingPreference] = useState(true);
  const [isRegeneratingAudio, setIsRegeneratingAudio] = useState(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlayingMessageId, setCurrentPlayingMessageId] = useState<string | null>(null);
  const [autoplayedMessageIds, setAutoplayedMessageIds] = useState<Set<string>>(new Set());
  const [generatingAudioIds, setGeneratingAudioIds] = useState<Set<string>>(new Set());
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<'voice' | 'limit'>('limit');
  
  // Extract current subscription tier from profile extensions
  const currentTier = profile?.extension?.find(
    ext => ext.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-tier'
  )?.valueString ?? 'Free'; // Default to 'Free' if not found
  
  // Static processing message
  const processingMessage = "Thinking... Please wait for a response";
  
  // Function to update autoplay preference in FHIR
  const updateAutoplaySetting = async (newValue: boolean): Promise<void> => {
    try {
      if (!profile?.id) {
        console.error('Cannot update setting: No profile ID');
        return;
      }

      // First, read the current Patient resource
      const patient = await medplum.readResource('Patient', profile.id);
      
      // Filter out existing autoplay extension if it exists
      const updatedExtensions = (patient.extension || []).filter(
        ext => ext.url !== 'https://progressnotes.app/fhir/StructureDefinition/ui-autoplay-enabled'
      );
      
      // Add the new extension with the updated value
      updatedExtensions.push({
        url: 'https://progressnotes.app/fhir/StructureDefinition/ui-autoplay-enabled',
        valueBoolean: newValue
      });
      
      // Update the Patient resource
      await medplum.updateResource({
        ...patient,
        extension: updatedExtensions
      });
      
      // Update local state
      setIsAutoplayEnabled(newValue);
      
      // If turning autoplay on, reset tracked messages
      if (newValue) {
        setAutoplayedMessageIds(new Set());
      }
    } catch (error) {
      console.error('Error updating autoplay setting:', error);
      // Still update the local state so the UI reflects the user's choice
      setIsAutoplayEnabled(newValue);
      if (newValue) {
        setAutoplayedMessageIds(new Set());
      }
    }
  };

  // Load autoplay preference from profile
  useEffect(() => {
    if (profile?.id) {
      setLoadingPreference(true);
      try {
        // Look for the autoplay extension
        const autoplayExtension = profile.extension?.find(
          ext => ext.url === 'https://progressnotes.app/fhir/StructureDefinition/ui-autoplay-enabled'
        );
        
        // If extension exists and has a value, use that value
        if (autoplayExtension?.valueBoolean !== undefined) {
          setIsAutoplayEnabled(autoplayExtension.valueBoolean);
        } 
        // Otherwise, use the default (true) but also save it to the profile
        else {
          updateAutoplaySetting(true).catch(console.error);
        }
      } catch (error) {
        console.error('Error loading autoplay preference:', error);
        // Keep the default (true)
      } finally {
        setLoadingPreference(false);
      }
    }
  }, [profile?.id]);
  
  // Set up conversation hook
  const { 
    thread, 
    isLoading, 
    error, 
    isBotProcessing,
    sendTextMessage, 
    sendAudioRecording,
    regenerateAudio: regenerateMessageAudio
  } = useConversation({ 
    threadId,
    skipTTS: showUpgradeForVoice // If showUpgradeForVoice is true, it means we're in Basic or Free tier
  });
  
  // Set up audio recording hook
  const {
    isRecording,
    audioBlob,
    status,
    stream,
    startRecording,
    stopRecording,
    warmupCompleted
  } = useAudioRecording();
  
  // Create a standalone scroll function to reuse
  const scrollToBottom = useCallback(() => {
    console.log('Scrolling to bottom called');
    
    // 1. First scroll the messages container
    const messagesContainer = document.getElementById('messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
    
    // 2. Then ensure input area is visible by scrolling window
    // Use a small delay to ensure container scroll completes first
    setTimeout(() => {
      // On mobile devices, also scroll the window to ensure input area is visible
      if (isMobileFullscreen) {
        console.log('Mobile detected, ensuring input area is visible');
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  }, [isMobileFullscreen]);

  // Enhanced scroll to bottom using ResizeObserver with more detailed tracking
  useEffect(() => {
    if (!messagesEndRef.current) return;
    
    // Scroll immediately when messages change
    scrollToBottom();
    
    // Set up ResizeObserver to detect actual height changes
    const messagesContainer = document.getElementById('messages-container');
    if (messagesContainer) {
      console.log('Setting up ResizeObserver on messages container');
      
      // Track the previous content to detect text changes even if height doesn't change
      let previousContent = messagesContainer.innerHTML;
      
      const resizeObserver = new ResizeObserver((entries) => {
        // Check if content has changed, not just size
        const currentContent = messagesContainer.innerHTML;
        const contentChanged = currentContent !== previousContent;
        
        if (contentChanged) {
          console.log('Content changed in messages container, scrolling to bottom');
          previousContent = currentContent;
          
          // When container content changes, scroll after a slight delay to ensure rendering completes
          setTimeout(() => {
            scrollToBottom();
          }, 50);
        } else {
          // If only size changed, scroll immediately
          scrollToBottom();
        }
      });
      
      // Also observe each individual message for content changes
      // This helps catch placeholder text being replaced with actual transcription
      const messageElements = messagesContainer.querySelectorAll('[data-message-id]');
      console.log(`Observing ${messageElements.length} individual messages for changes`);
      
      messageElements.forEach(element => {
        resizeObserver.observe(element);
      });
      
      // Start observing the container
      resizeObserver.observe(messagesContainer);
      
      // Clean up
      return () => {
        console.log('Cleaning up ResizeObserver');
        resizeObserver.disconnect();
      };
    }
  }, [thread?.messages.length, thread?.messages, scrollToBottom]); // Added thread.messages to dependencies
  
  // Additional scroll trigger when bot processing state changes
  // This ensures we scroll after transcription completes
  useEffect(() => {
    if (!isBotProcessing) {
      console.log('Bot processing state changed to false, triggering scroll');
      // Add slight delay to ensure render completes
      setTimeout(scrollToBottom, 300);
    }
  }, [isBotProcessing, scrollToBottom]);
  
  // Add a MutationObserver to directly watch for content changes
  // This is especially helpful for catching transcription replacements on desktop
  useEffect(() => {
    const messagesContainer = document.getElementById('messages-container');
    if (!messagesContainer) return;
    
    console.log('Setting up MutationObserver for message content changes');
    
    // Create a MutationObserver to watch for changes in the DOM content
    const observer = new MutationObserver((mutations) => {
      console.log(`Detected ${mutations.length} mutations in message container`);
      
      // Check for transcription-related mutations
      const hasTranscriptionChanges = mutations.some(mutation => {
        // Check if this mutation involves a transcription placeholder being replaced
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          const targetNode = mutation.target as Node;
          const nodeContent = targetNode.textContent || '';
          
          // If we detect a change from or to transcribing text
          return (
            nodeContent.includes('Transcribing audio') || 
            (mutation.oldValue && mutation.oldValue.includes('Transcribing audio'))
          );
        }
        return false;
      });
      
      // If we detect transcription-related changes, scroll to show the new content
      if (hasTranscriptionChanges) {
        console.log('Detected transcription text change, ensuring scroll to bottom');
        // Use a larger delay for transcription changes to ensure rendering completes
        setTimeout(scrollToBottom, 150);
      } else {
        // For other changes, scroll with a shorter delay
        setTimeout(scrollToBottom, 50);
      }
    });
    
    // Options for the observer
    const config = { 
      childList: true,      // Observe direct children
      subtree: true,        // Observe all descendants
      characterData: true,  // Observe text changes
      characterDataOldValue: true // Keep old text values for comparison
    };
    
    // Start observing
    observer.observe(messagesContainer, config);
    
    // Cleanup
    return () => {
      observer.disconnect();
      console.log('MutationObserver disconnected');
    };
  }, [scrollToBottom]);

  // Autoplay audio for new assistant messages (if enabled)
  useEffect(() => {
    if (!isAutoplayEnabled || isMuted || !thread?.messages?.length) return;
    
    // Get the last message
    const lastMessage = thread.messages[thread.messages.length - 1];
    
    // Check if message was sent within the last 2 minutes
    const isRecentMessage = () => {
      const now = new Date();
      const messageTime = lastMessage.sentAt;
      const diffMs = now.getTime() - messageTime.getTime();
      const diffMinutes = diffMs / (1000 * 60);
      return diffMinutes <= 2; // Only autoplay if message is within last 2 minutes
    };
    
    // Check if it's from the assistant, can have audio, is recent, and hasn't been autoplayed yet
    if (lastMessage && 
        lastMessage.role === 'assistant' && 
        lastMessage.canHaveAudio && // Use new canHaveAudio property
        !isPlaying &&
        isRecentMessage() &&
        !autoplayedMessageIds.has(lastMessage.id) &&
        !generatingAudioIds.has(lastMessage.id)) { // Don't autoplay if already generating
      
      // If no audio data but available on demand, generate it first
      if (!lastMessage.audioData && lastMessage.hasAudioOnDemand) {
        console.log('Generating audio for autoplay:', lastMessage.id);
        
        // Generate audio then play
        const generateAndPlay = async () => {
          setGeneratingAudioIds(prev => new Set(prev).add(lastMessage.id));
          try {
            const success = await regenerateMessageAudio(lastMessage.id);
            if (success) {
              console.log('Audio generated successfully for autoplay');
              // Audio will autoplay after thread refreshes with the new audio data
            } else {
              console.error('Failed to generate audio for autoplay');
            }
          } catch (error) {
            console.error('Error generating audio for autoplay:', error);
          } finally {
            setGeneratingAudioIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(lastMessage.id);
              return newSet;
            });
          }
        };
        
        // Delay slightly to ensure UI is ready
        setTimeout(generateAndPlay, 500);
        return;
      }
      
      // Normal autoplay for messages with existing audio
      console.log('Setting up audio playback with delay to ensure scrolling completes');
      setTimeout(() => {
        // Double-check we're still in the right state to play
        if (!isMuted && !isPlaying && isAutoplayEnabled) {
          console.log('Starting audio playback for message:', lastMessage.id);
          
          // Mark this message as autoplayed
          setAutoplayedMessageIds(prev => {
            const newSet = new Set(prev);
            newSet.add(lastMessage.id);
            return newSet;
          });
          
          playAudio(lastMessage.id, lastMessage.audioData!, lastMessage.audioContentType);
        }
      }, 1000); // Increased from 500ms to 1000ms
    }
  }, [thread?.messages?.length, isAutoplayEnabled, isMuted, isPlaying, autoplayedMessageIds, generatingAudioIds, regenerateMessageAudio]);
  
  // Handle sending text message
  const handleSendMessage = async () => {
    if (!userInput.trim()) return;
    
    // Check if user needs to upgrade to send messages (free tier restriction)
    if (showUpgradeForMessage) {
      setUpgradeFeature('limit');
      setShowUpgradeModal(true);
      return;
    }
    
    const messageText = userInput; // Store the input value
    setUserInput(''); // Clear input immediately before async operation
    
    try {
      await sendTextMessage(messageText);
    } catch (error) {
      // Restore the input text if sending fails
      setUserInput(messageText);
      showNotification({
        title: 'Error',
        message: `Failed to send message: ${(error as Error).message}`,
        color: 'red'
      });
    }
  };
  
  // Handle recording controls - matches progress2-base approach
  const handleToggleRecording = async () => {
    console.log('[handleToggleRecording] Starting function with state:', { 
      isRecording, 
      showUpgradeForVoice, 
      currentTier,
      isBotProcessing,
      isPlaying
    });
    
    // First, handle the case when already recording - always allow stopping
    if (isRecording) {
      console.log('[handleToggleRecording] User is already recording, proceeding to stop recording');
      try {
        console.log('[handleToggleRecording] Stopping recording...');
        const blob = await stopRecording();
        console.log('[handleToggleRecording] Recording stopped, blob size:', blob.size, 'type:', blob.type);
        
        if (blob.size > 0) {
          // This exact call is what happens in progress2-base
          console.log('[handleToggleRecording] Sending audio blob to sendAudioRecording...');
          await sendAudioRecording(blob);
        } else {
          console.log('[handleToggleRecording] Blob is empty, not sending');
        }
      } catch (error) {
        console.error('[handleToggleRecording] Error in stopRecording:', error);
        showNotification({
          title: 'Error',
          message: `Recording failed: ${(error as Error).message}`,
          color: 'red'
        });
      }
      console.log('[handleToggleRecording] Early return after stopping recording');
      return; // Early return after stopping recording
    }
    
    // At this point, we know we're not recording
    console.log('[handleToggleRecording] User is not recording, checking permissions');
    
    // Check if user needs to upgrade to use voice (basic or free tier restriction)
    if (showUpgradeForVoice) {
      console.log('[handleToggleRecording] User needs to upgrade for voice, showing modal. showUpgradeForVoice =', showUpgradeForVoice);
      console.log('[handleToggleRecording] Setting upgradeFeature to "voice"');
      setUpgradeFeature('voice');
      console.log('[handleToggleRecording] Setting showUpgradeModal to true');
      setShowUpgradeModal(true);
      console.log('[handleToggleRecording] Modal shown, early return');
      return; // Early return if upgrade needed
    }
    
    // If we get here, we're not recording and user is allowed to record
    console.log('[handleToggleRecording] User has permission, proceeding to start recording');
    try {
      console.log('[handleToggleRecording] Starting recording...');
      await startRecording();
      console.log('[handleToggleRecording] Recording started successfully');
    } catch (error) {
      console.error('[handleToggleRecording] Error starting recording:', error);
      showNotification({
        title: 'Error',
        message: `Couldn't start recording: ${(error as Error).message}`,
        color: 'red'
      });
    }
  };
  
  // Function to play audio
  const playAudio = (messageId: string, base64Audio: string, contentType: string = 'audio/wav') => {
    if (!base64Audio || isMuted) return;
    
    try {
      // If already playing, stop current playback
      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
        
        // If the same message is being played, just pause it and return
        if (currentPlayingMessageId === messageId) {
          setIsPlaying(false);
          setCurrentPlayingMessageId(null);
          return;
        }
      }
      
      // Convert base64 to blob
      const byteCharacters = atob(base64Audio);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });
      
      // Create object URL for audio element
      const url = URL.createObjectURL(blob);
      
      if (audioRef.current) {
        audioRef.current.src = url;
        
        // Set up event listeners
        audioRef.current.onloadedmetadata = () => {
          audioRef.current?.play().catch(err => {
            console.error('Error playing audio:', err);
            setIsPlaying(false);
            setCurrentPlayingMessageId(null);
          });
          
          setIsPlaying(true);
          setCurrentPlayingMessageId(messageId);
        };
        
        audioRef.current.onended = () => {
          URL.revokeObjectURL(url);
          setIsPlaying(false);
          setCurrentPlayingMessageId(null);
        };
        
        audioRef.current.onpause = () => {
          setIsPlaying(false);
        };
        
        audioRef.current.onplay = () => {
          setIsPlaying(true);
          setCurrentPlayingMessageId(messageId);
        };
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
      setCurrentPlayingMessageId(null);
    }
  };
  
  // Function to pause audio
  const pauseAudio = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause();
      // State will be updated via the onpause event listener
    }
  };
  
  // Show loading state
  if (isLoading && !thread) {
    return (
      <Paper p="xl" withBorder style={{ height: '500px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Stack align="center">
          <Loader size="md" />
          <Text mt="md">Loading conversation...</Text>
        </Stack>
      </Paper>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <Paper p="xl" withBorder style={{ height: '500px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Stack align="center">
          <Text color="red" size="lg">Error loading conversation</Text>
          <Text>{error}</Text>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Stack>
      </Paper>
    );
  }
  
  // Simple and reliable 3-part flex layout that works on mobile
  return (
    <Paper p={0} withBorder style={{ 
      height: isMobileFullscreen ? '100%' : '500px',
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
      maxWidth: '100vw', // Prevent expanding beyond viewport width
      width: '100%'      // Take up full width but no more
    }}>
      <audio ref={audioRef} style={{ display: 'none' }} />
      {/* 1. HEADER - flex-shrink: 0 keeps it fixed size */}
      <Box style={{
        borderBottom: '1px solid var(--mantine-color-gray-3)',
        backgroundColor: 'white',
        flexShrink: 0
      }}>
        <Group p="md" justify="space-between">
          <Group>
            <Avatar radius="xl" color="blue">RG</Avatar>
            <Text fw={600}>Reflection Guide</Text>
          </Group>
          <Group gap="xs">
            {/* Autoplay toggle moved to header */}
            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">Autoplay</Text>
              <Switch 
                size="xs"
                checked={isAutoplayEnabled}
                disabled={loadingPreference}
                onChange={(event) => {
                  const newValue = event.currentTarget.checked;
                  updateAutoplaySetting(newValue).catch(console.error);
                }}
                color="green"
              />
            </Group>
            <Tooltip label={isMuted ? "Unmute audio" : "Mute audio"}>
              <ActionIcon 
                variant={isMuted ? "filled" : "subtle"} 
                color={isMuted ? "gray" : "blue"}
                onClick={() => setIsMuted(!isMuted)}
              >
                {isMuted ? <IconVolumeOff size={18} /> : <IconVolume size={18} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>
      {/* 2. MESSAGES - flex: 1 allows it to fill available space */}
      <Box 
        id="messages-container" 
        style={{ 
          flex: 1,
          overflowY: 'auto',
          padding: '16px'
        }}
      >
          {thread?.messages.map((message, index) => {
            // Debug logging for audio state
            console.log('Message audio state:', {
              id: message.id,
              role: message.role,
              text: message.text.substring(0, 50) + '...',
              hasAudioData: !!message.audioData,
              hasAudioOnDemand: message.hasAudioOnDemand,
              canHaveAudio: message.canHaveAudio,
              shouldShowAudioControls: message.role === 'assistant' && (message.audioData || message.hasAudioOnDemand)
            });
            
            return (
          <Box 
            key={message.id}
            data-message-id={message.id} // Add data attribute for ResizeObserver targeting
            mb="md"
            style={{ 
              maxWidth: '80%', 
              marginLeft: message.role === 'assistant' ? 0 : 'auto', 
              marginRight: message.role === 'user' ? 0 : 'auto' 
            }}
          >
            {/* Timestamp shown only when there's a significant time gap */}
            {(() => {
              // Check if we should show timestamp (first message or significant time gap)
              const showTimestamp = (() => {
                // Always show timestamp for first message
                if (index === 0) return true;
                
                // Get previous message time
                const prevMessage = thread.messages[index - 1];
                const prevDate = prevMessage.sentAt;
                const currentDate = message.sentAt;
                
                // Calculate time difference in minutes
                const timeDiffMinutes = (currentDate.getTime() - prevDate.getTime()) / (1000 * 60);
                
                // Show timestamp if there's a significant gap (40+ minutes)
                return timeDiffMinutes >= 40;
              })();
              
              if (showTimestamp) {
                const date = message.sentAt;
                const now = new Date();
                
                // Format time as hours:minutes AM/PM
                const timeOptions: Intl.DateTimeFormatOptions = { 
                  hour: 'numeric', 
                  minute: '2-digit',
                  hour12: true 
                };
                const timeStr = date.toLocaleTimeString(undefined, timeOptions);
                
                // Check if message was sent today
                const isToday = date.toDateString() === now.toDateString();
                
                // Check if message was sent yesterday
                const yesterday = new Date(now);
                yesterday.setDate(now.getDate() - 1);
                const isYesterday = date.toDateString() === yesterday.toDateString();
                
                // Check if message was sent within the last week
                const oneWeekAgo = new Date(now);
                oneWeekAgo.setDate(now.getDate() - 7);
                const isWithinLastWeek = date >= oneWeekAgo;
                
                let formattedDate;
                if (isToday) {
                  formattedDate = `Today ${timeStr}`;
                } else if (isYesterday) {
                  formattedDate = `Yesterday ${timeStr}`;
                } else if (isWithinLastWeek) {
                  // Format to day of week (e.g., "Monday 7:07 PM")
                  const dayOfWeek = date.toLocaleDateString(undefined, { weekday: 'long' });
                  formattedDate = `${dayOfWeek} ${timeStr}`;
                } else {
                  // Format date for older messages (e.g., "Apr 26 7:07 PM")
                  const dateOptions: Intl.DateTimeFormatOptions = { 
                    month: 'short', 
                    day: 'numeric'
                  };
                  formattedDate = `${date.toLocaleDateString(undefined, dateOptions)} ${timeStr}`;
                }
                
                return (
                  <Text 
                    size="xs" 
                    c="dimmed" 
                    ta={message.role === 'assistant' ? "left" : "right"}
                    mb={4}
                  >
                    {formattedDate}
                  </Text>
                );
              }
              
              return null;
            })()}
            
            <Paper 
              p="md" 
              radius="lg"
              style={{
                backgroundColor: message.role === 'assistant' ? 'var(--mantine-color-blue-0)' : 'var(--mantine-color-gray-0)',
              }}
            >
              {/* Special handling for transcription placeholder message */}
              {message.text === "[Audio message - Transcribing...]" ? (
                <Group gap={8} data-transcribing="true">
                  <Loader size="xs" />
                  <Text c="dimmed">Transcribing audio...</Text>
                </Group>
              ) : (
                <Text style={{ whiteSpace: 'pre-wrap' }}>
                  {message.text.trim()}
                </Text>
              )}
            </Paper>
            
            {/* Audio playback for assistant messages */}
            {message.role === 'assistant' && (message.audioData || message.hasAudioOnDemand) && (
              <Group mt="xs" justify="flex-start">
                <Button 
                  variant={isPlaying && currentPlayingMessageId === message.id ? "light" : "subtle"}
                  size="xs"
                  leftSection={
                    generatingAudioIds.has(message.id) ? (
                      <Loader size={14} />
                    ) : isPlaying && currentPlayingMessageId === message.id ? (
                      <IconPlayerPause size={14} />
                    ) : (
                      <IconPlayerPlay size={14} />
                    )
                  }
                  onClick={async () => {
                    // If generating audio, do nothing
                    if (generatingAudioIds.has(message.id)) return;
                    
                    // If no audio data but available on demand, generate it first
                    if (!message.audioData && message.hasAudioOnDemand) {
                      setGeneratingAudioIds(prev => new Set(prev).add(message.id));
                      try {
                        const success = await regenerateMessageAudio(message.id);
                        if (!success) {
                          showNotification({
                            title: 'Error',
                            message: 'Failed to generate audio',
                            color: 'red'
                          });
                        }
                        // Thread will refresh automatically, audio will be available after
                      } catch (error) {
                        showNotification({
                          title: 'Error',
                          message: 'Failed to generate audio',
                          color: 'red'
                        });
                      } finally {
                        setGeneratingAudioIds(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(message.id);
                          return newSet;
                        });
                      }
                      return;
                    }
                    
                    // Normal play/pause logic
                    if (isPlaying && currentPlayingMessageId === message.id) {
                      pauseAudio();
                    } else {
                      playAudio(message.id, message.audioData!, message.audioContentType);
                    }
                  }}
                  disabled={isMuted || generatingAudioIds.has(message.id)}
                  color="blue"
                  styles={isPlaying && currentPlayingMessageId === message.id ? {
                    root: { fontWeight: 600 }
                  } : undefined}
                >
                  {generatingAudioIds.has(message.id) 
                    ? 'Generating...' 
                    : isPlaying && currentPlayingMessageId === message.id 
                      ? 'Pause' 
                      : 'Play Audio'}
                </Button>
                
                <Menu shadow="md" position="bottom-end">
                  <Menu.Target>
                    <ActionIcon size="xs" variant="subtle">
                      <IconDotsVertical size={14} />
                    </ActionIcon>
                  </Menu.Target>
                  
                  <Menu.Dropdown>
                    <Menu.Item 
                      leftSection={<IconDownload size={14} />}
                      onClick={() => {
                        if (!message.audioData) return;
                        
                        // Create blob from base64
                        const byteCharacters = atob(message.audioData);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        
                        // Force 'audio/mp3' as the content type
                        const blob = new Blob([byteArray], { type: 'audio/mp3' });
                        
                        // Create download link
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        
                        // Always use .mp3 extension
                        a.download = `reflection-guide-audio-${message.id}.mp3`;
                        
                        document.body.appendChild(a);
                        a.click();
                        
                        // Clean up
                        setTimeout(() => {
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }, 100);
                      }}
                    >
                      Download Audio
                    </Menu.Item>
                    
                    <Menu.Item 
                      leftSection={
                        generatingAudioIds.has(message.id) ? <Loader size={14} /> : <IconRefresh size={14} />
                      }
                      onClick={async () => {
                        if (generatingAudioIds.has(message.id)) return; // Prevent duplicate requests
                        
                        try {
                          setIsRegeneratingAudio(true);
                          setRegeneratingMessageId(message.id);
                          setGeneratingAudioIds(prev => new Set(prev).add(message.id));
                          
                          const success = await regenerateMessageAudio(message.id);
                          
                          if (success) {
                            showNotification({
                              title: 'Success',
                              message: 'Voice regenerated successfully',
                              color: 'green'
                            });
                          }
                        } catch (error) {
                          showNotification({
                            title: 'Error',
                            message: `Failed to regenerate voice: ${(error as Error).message}`,
                            color: 'red'
                          });
                        } finally {
                          setIsRegeneratingAudio(false);
                          setRegeneratingMessageId(null);
                          setGeneratingAudioIds(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(message.id);
                            return newSet;
                          });
                        }
                      }}
                      disabled={isRegeneratingAudio || generatingAudioIds.has(message.id)}
                    >
                      {(isRegeneratingAudio && regeneratingMessageId === message.id) || generatingAudioIds.has(message.id) 
                        ? 'Regenerating...' 
                        : 'Regenerate Voice'}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}
          </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>
      {/* 3. FOOTER - flex-shrink: 0 keeps it fixed size */}
      <Box style={{
        borderTop: '1px solid var(--mantine-color-gray-3)',
        backgroundColor: 'white',
        flexShrink: 0,
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.05)'
      }}>
        <Paper 
          p="md" 
          style={{ 
            boxShadow: 'none', 
            border: 'none'
          }}
        >
        <Group style={{ maxWidth: '100%', width: '100%', overflow: 'hidden' }}>
          {isRecording || isBotProcessing ? (
            /* Status message that REPLACES the text input when recording or processing */
            (<Box style={{ 
              flex: 1,
              height: '36px', // Match height of TextInput for consistent sizing
              padding: '0 12px',
              borderRadius: 'var(--mantine-radius-sm)',
              backgroundColor: 'var(--mantine-color-gray-0)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {isRecording && (
                <>
                  <Loader size="xs" />
                  <Text size="sm" c={!warmupCompleted ? "yellow" : "dimmed"}>
                    {isRecording && !warmupCompleted
                      ? 'Preparing microphone...' 
                      : 'Recording... Click microphone to stop'}
                  </Text>
                </>
              )}
              {isBotProcessing && !isRecording && (
                <>
                  <Loader size="xs" color="blue"/>
                  <Text size="sm" c="blue">
                    {processingMessage}
                  </Text>
                </>
              )}
            </Box>)
          ) : (
            /* Normal text input when not recording or processing */
            (<TextInput
              placeholder="Type your message..."
              value={userInput}
              onChange={(e) => setUserInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              style={{ 
                flex: 1,
                maxWidth: '100%',
                fontSize: '16px' // Prevents iOS zoom on input focus
              }}
              rightSectionWidth={42}
              rightSection={
                <ActionIcon
                  type="submit"
                  onClick={handleSendMessage}
                  disabled={!userInput.trim()}
                  color="blue"
                >
                  <IconSend size={18} />
                </ActionIcon>
              }
            />)
          )}
          
          {/* Microphone button - always visible */}
          <Tooltip label={
              isRecording 
                ? "Stop recording" 
                : isBotProcessing 
                  ? "Please wait..." 
                  : isPlaying 
                    ? "Finish playing audio first" 
                    : "Start recording"
            }>
            <ActionIcon
              size="xl" 
              radius="xl"
              variant={isRecording ? "filled" : "outline"}
              color={isRecording && !warmupCompleted ? "yellow" : isRecording ? "red" : "blue"}
              onClick={handleToggleRecording}
              disabled={(isBotProcessing && !isRecording) || (isPlaying && !isRecording)} // Disable when bot is processing OR audio is playing (unless already recording)
              style={{
                width: 48,
                height: 48,
                marginLeft: 8,
                boxShadow: isRecording ? '0 0 10px rgba(255, 0, 0, 0.5)' : undefined
              }}
            >
              {isRecording ? <IconMicrophoneOff size={24} /> : <IconMicrophone size={24} />}
            </ActionIcon>
          </Tooltip>
        </Group>
        </Paper>
      </Box>
      {/* Upgrade Modal */}
      <UpgradeModal
        opened={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
        currentTier={currentTier}
      />
    </Paper>
  );
}